import { apiClient } from "./client";

// Order interfaces
export interface OrderRequest {
  accountId: string;
  instrumentId: string;
  product: string;
  category: string;
  stake: number;
  currency: string;
  product_params: Record<string, any>;
}

export interface OrderResponse {
  orderId: string;
  accountId: string;
  instrumentId: string;
  product: string;
  category: string;
  stake: number;
  payout: number;
  return_percent: number;
  product_params: Record<string, any>;
  entry_spot: number;
  entry_time: string;
  expiry_time: string;
  status:
    | "open"
    | "filled"
    | "cancelled"
    | "rejected"
    | "expired"
    | "won"
    | "lost";
  currency: string;
  current_spot?: number;
  current_pnl?: {
    amount: number;
    percentage: number;
  };
}

export interface OrderListResponse {
  orders: OrderResponse[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export interface OrderValidationRequest
  extends Omit<OrderRequest, "accountId"> {
  accountId?: string;
}

export interface OrderValidationResponse {
  valid: boolean;
  stake: number;
  payout: number;
  return_percent: number;
  spot: number;
  strike: number;
  expiry_time: string;
  base_probability: number;
  risk_markup: number;
  commission: number;
  validation: Record<string, string>;
  error?: string;
}

export interface CancelOrderResponse {
  orderId: string;
  status: "cancelled";
  cancelledAt: string;
}

class OrderService {
  /**
   * Place a new order
   */
  async placeOrder(request: OrderRequest): Promise<OrderResponse> {
    try {
      const response = await apiClient.post<OrderResponse>(
        "/v1/orders",
        request
      );
      return response;
    } catch (error) {
      console.error("[OrderService] Failed to place order:", error);
      throw this.handleOrderError(error, "Failed to place order");
    }
  }

  /**
   * Get list of orders for an account
   */
  async getOrders(params: {
    accountId: string;
    status?: string;
    from?: string;
    to?: string;
    page?: number;
    limit?: number;
  }): Promise<OrderListResponse> {
    // Build query string manually for GET request
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    try {
      const response = await apiClient.get<OrderListResponse>(
        `/v1/orders?${queryParams.toString()}`
      );
      return response;
    } catch (error) {
      console.error("[OrderService] Failed to fetch orders:", error);
      throw this.handleOrderError(error, "Failed to fetch orders");
    }
  }

  /**
   * Get specific order details
   */
  async getOrder(orderId: string): Promise<OrderResponse> {
    try {
      const response = await apiClient.get<OrderResponse>(
        `/v1/orders/${orderId}`
      );
      return response;
    } catch (error) {
      console.error("[OrderService] Failed to fetch order:", error);
      throw this.handleOrderError(error, "Failed to fetch order details");
    }
  }

  /**
   * Cancel an open order
   */
  async cancelOrder(orderId: string): Promise<CancelOrderResponse> {
    try {
      const response = await apiClient.delete<CancelOrderResponse>(
        `/v1/orders/${orderId}`
      );
      return response;
    } catch (error) {
      console.error("[OrderService] Failed to cancel order:", error);
      throw this.handleOrderError(error, "Failed to cancel order");
    }
  }

  /**
   * Validate order parameters without placing
   */
  async validateOrder(
    request: OrderValidationRequest
  ): Promise<OrderValidationResponse> {
    try {
      const response = await apiClient.post<OrderValidationResponse>(
        "/v1/orders/validate",
        request
      );
      return response;
    } catch (error) {
      console.error("[OrderService] Order validation failed:", error);
      throw this.handleOrderError(error, "Order validation failed");
    }
  }

  /**
   * Get product category for a product type
   */
  getProductCategory(productType: string): string {
    const categoryMap: Record<string, string> = {
      rise_fall: "directional",
      higher_lower: "directional",
      matches_differs: "digit-based",
      even_odd: "digit-based",
      over_under: "digit-based",
      accumulator: "growth-based",
    };

    return categoryMap[productType] || "directional";
  }

  /**
   * Format product parameters for order placement
   */
  formatOrderParams(
    productType: string,
    params: Record<string, any>
  ): Record<string, any> {
    // Use consistent parameter formatting similar to quote service
    const formatted = { ...params };

    switch (productType) {
      case "rise_fall":
        // Ensure direction field is used consistently
        if (params.direction) {
          formatted.contract_type = params.direction;
          delete formatted.direction;
        }
        break;
      case "higher_lower":
        // Keep direction field for higher/lower
        if (params.direction) {
          formatted.direction = params.direction;
        }
        break;
      case "matches_differs":
      case "even_odd":
      case "over_under":
        // Keep direction field for digit-based products
        if (params.direction) {
          formatted.direction = params.direction;
        }
        break;
      case "accumulator":
        // Keep as-is for accumulator
        break;
      default:
        break;
    }

    return formatted;
  }

  /**
   * Create order request from trade parameters
   */
  createOrderRequest(params: {
    accountId: string;
    instrumentId: string;
    product: string;
    stake: number;
    currency: string;
    productParams: Record<string, any>;
  }): OrderRequest {
    const { accountId, instrumentId, product, stake, currency, productParams } =
      params;

    return {
      accountId,
      instrumentId,
      product,
      category: this.getProductCategory(product),
      stake,
      currency,
      product_params: this.formatOrderParams(product, productParams),
    };
  }

  /**
   * Handle order-specific errors
   */
  private handleOrderError(error: any, defaultMessage: string): Error {
    // Handle ApiClientError from our custom API client
    if (error?.errorCode) {
      const errorCode = error.errorCode;
      const message = error.message;

      // Handle specific order errors
      switch (errorCode) {
        case "insufficient_balance":
          return new Error("Insufficient account balance for this trade");
        case "invalid_parameters":
          return new Error(`Invalid order parameters: ${message}`);
        case "market_closed":
          return new Error("Market is currently closed for trading");
        case "order_not_found":
          return new Error("Order not found or has been removed");
        case "cannot_cancel":
          return new Error("Order cannot be cancelled at this time");
        case "order_already_filled":
          return new Error("Order has already been filled");
        case "stake_too_low":
          return new Error("Stake amount is below minimum required");
        case "stake_too_high":
          return new Error("Stake amount exceeds maximum allowed");
        case "unprocessable_entity":
          return new Error(`Order validation failed: ${message}`);
        case "forbidden":
          return new Error("Insufficient permissions for this operation");
        case "unauthorized":
          return new Error("Authentication required to place orders");
        default:
          return new Error(message || defaultMessage);
      }
    }

    if (error?.message) {
      return new Error(error.message);
    }

    return new Error(defaultMessage);
  }
}

// Export singleton instance
export const orderService = new OrderService();
export default orderService;
